import React, { useState, useEffect } from 'react';
import type { Note } from '../../services/notesApi.ts';

interface CaptureNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (note: { title: string; content: string; color: string }) => void;
  initialData?: Note | null;
}

const COLORS: { id: 'yellow' | 'blue' | 'red' | 'green'; swatch: string }[] = [
  { id: 'yellow', swatch: 'var(--app-warning-border)' },
  { id: 'blue', swatch: 'var(--app-primary-soft-2)' },
  { id: 'red', swatch: 'var(--app-danger-border)' },
  { id: 'green', swatch: 'var(--app-success-border)' },
];

const CaptureNoteModal: React.FC<CaptureNoteModalProps> = ({ isOpen, onClose, onAdd, initialData }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [color, setColor] = useState<'yellow' | 'blue' | 'red' | 'green'>('yellow');

  useEffect(() => {
    if (isOpen && initialData) {
      setTitle(initialData.title || '');
      setContent(initialData.content || '');
      setColor(initialData.color || 'yellow');
    } else if (isOpen && !initialData) {
      setTitle('');
      setContent('');
      setColor('yellow');
    }
  }, [isOpen, initialData]);

  const canSave = title.trim() && content.trim();

  const handleSubmit = () => {
    if (!canSave) return;
    onAdd({ title: title.trim(), content: content.trim(), color });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-lg">
        <h2 className="text-xl font-bold mb-4">{initialData ? 'Edit Note' : 'New Note'}</h2>
        <div className="space-y-3">
          <input
            className="w-full border border-[color:var(--app-border-strong)] rounded-lg p-2 text-sm"
            placeholder="Note title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
          />
          <textarea
            className="w-full border border-[color:var(--app-border-strong)] rounded-lg p-2 text-sm"
            placeholder="Write your note..."
            rows={5}
            value={content}
            onChange={e => setContent(e.target.value)}
          />
          <div>
            <div className="text-xs font-medium text-[color:var(--lyra-color-text-muted)] mb-1.5">Color</div>
            <div className="flex gap-2">
              {COLORS.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setColor(c.id)}
                  title={c.id}
                  style={{
                    width: 26, height: 26, borderRadius: '50%', background: c.swatch,
                    border: color === c.id ? '2px solid var(--lyra-color-primary)' : '2px solid transparent',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6 space-x-2">
          <button
            className="px-4 py-2 rounded text-sm font-semibold bg-white border border-[color:var(--app-border-strong)] text-[color:var(--app-text-2)] hover:bg-[color:var(--lyra-color-surface)] transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded text-sm font-semibold text-white transition-colors"
            style={{ background: canSave ? 'var(--lyra-color-primary)' : 'var(--app-text-3)', cursor: canSave ? 'pointer' : 'not-allowed' }}
            onMouseEnter={e => { if (canSave) e.currentTarget.style.background = 'var(--app-primary-deep)'; }}
            onMouseLeave={e => { if (canSave) e.currentTarget.style.background = 'var(--lyra-color-primary)'; }}
            onClick={handleSubmit}
            disabled={!canSave}
          >
            {initialData ? 'Save Changes' : 'Add Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaptureNoteModal;
